<template>
    <Slider
        v-bind="primeVueProps"
        :class="primeVueClasses"
        :pt="sliderPassthrough"
        @update:modelValue="$emit('update:modelValue', $event)"
    />
</template>

<script>
import Slider from 'primevue/slider';
/**
 * ESlider - Abstraction wrapper around PrimeVue's Slider
 *
 * Usage:
 *   <e-slider v-model="value" :min="0" :max="100">
 *   </e-slider>
 *
 *   <e-slider v-model="volume" :step="5" type="is-success">
 *   </e-slider>
 *
 *   <e-slider v-model="range" :min="0" :max="100" range>
 *   </e-slider>
 *
 *   <e-slider v-model="price" :min="0" :max="1000" :step="10" size="is-large">
 *   </e-slider>
 *
 *   <e-slider v-model="value" orientation="vertical">
 *   </e-slider>
 *
 *   <e-slider v-model="value" :force-prime-vue="true">
 *   </e-slider>
 *
 * All Buefy b-slider and PrimeVue Slider props, events, and slots are supported.
 *
 */
export default {
    name: 'ESlider',
    components: {
        Slider,
    },
    inheritAttrs: false,
    emits: ['update:modelValue'],
    computed: {
        primeVueProps() {
            const props = { ...this.$attrs };

            // Remove Buefy-specific props that don't apply to PrimeVue
            delete props.type; // Buefy uses type for colors (mapped in sliderPassthrough)
            delete props.size; // Size is handled via CSS classes in PrimeVue
            delete props.tooltip; // PrimeVue doesn't have built-in tooltip prop
            delete props.lazy; // Buefy specific
            delete props['custom-formatter']; // Buefy specific
            delete props['aria-label']; // Will be handled separately if needed
            delete props.indicator; // Buefy specific
            delete props.ticks; // Buefy specific

            // Map 'range' prop - both libraries use the same name
            // Map modelValue/value - both use v-model
            // Map min, max, step, disabled, orientation - all the same

            return props;
        },
        primeVueClasses() {
            const classes = [];

            // Add width class for horizontal sliders (default is w-56 in PrimeVue docs)
            if (!this.$attrs.orientation || this.$attrs.orientation === 'horizontal') {
                classes.push('w-full');
            } else if (this.$attrs.orientation === 'vertical') {
                classes.push('h-56');
            }
            classes.push('my-2');
            return classes.join(' ');
        },
        sliderPassthrough() {
            // Determine track height and handle size based on Buefy size prop
            let trackHeight = '8px';
            let handleSize = '18px';
            let handleMargin = '-9px';

            if (this.$attrs.size === 'is-small') {
                trackHeight = '6px';
                handleSize = '16px';
                handleMargin = '-7px';
            } else if (this.$attrs.size === 'is-large') {
                trackHeight = '1rem';
                handleSize = '24px';
                handleMargin = '-10px';
            }

            // Map Buefy type to CSS class that uses SCSS variables
            let rangeColorClass = 'slider-range-primary'; // default
            if (this.$attrs.type) {
                const typeMap = {
                    'is-primary': 'slider-range-primary',
                    'is-success': 'slider-range-success',
                    'is-danger': 'slider-range-danger',
                    'is-warning': 'slider-range-warning',
                    'is-info': 'slider-range-info',
                    'is-link': 'slider-range-link',
                    primary: 'slider-range-primary',
                    success: 'slider-range-success',
                    danger: 'slider-range-danger',
                    warning: 'slider-range-warning',
                    info: 'slider-range-info',
                    link: 'slider-range-link',
                };
                rangeColorClass = typeMap[this.$attrs.type] || 'slider-range-primary';
            }

            const rangeStyle = `height: ${trackHeight};`;
            return {
                root: {
                    class: 'pt-2',
                    style: `padding-top: 1rem; ${rangeStyle}`,
                },
                range: {
                    style: rangeStyle,
                    class: rangeColorClass,
                },
                handle: {
                    style: `width: ${handleSize}; height: ${handleSize}; margin-top: ${handleMargin}; margin-left: ${handleMargin};`,
                    'data-pc-section': 'handle',
                },
            };
        },
    },
};
</script>

<style scoped lang="scss">
@import '~assets/scss/toolbox.scss';

/* Slider range colors using SCSS theme variables */
.slider-range-primary {
    background-color: $primary !important;
}

.slider-range-success {
    background-color: $success !important;
}

.slider-range-danger {
    background-color: $danger !important;
}

.slider-range-warning {
    background-color: $warning !important;
}

.slider-range-info {
    background-color: $info !important;
}

.slider-range-link {
    background-color: $link !important;
}

/* Add a thin border to PrimeVue slider handle for definition */
.p-slider .p-slider-handle {
    border: 1px solid rgba($grey, 0.3) !important;
}
</style>
